import { useCallback, useEffect, useRef, useState } from "react";
import { StepBars } from "../../components/step-bars";
import { DualScreenLayout } from "../../components/dual-screen-layout";
import { StepFormContainer } from "../../components/step-form";
import {
  DesignerApplicationSteps,
  DesignerApplication,
  UserFromServer,
} from "../../__generated__/types";
import { WorkExperienceForm } from "./work-experience-form";
import "./styles.scss";
import { ReferenceForm } from "./reference-form";
// ⬇️ updated import to match your new file location/name
import { BusinessGoalsAndMetricsForm } from "./business-goals-form";
import { EducationForm } from "./education-form";
import { useUserContext } from "../../hooks/userContext";
import { useNavigate } from "react-router";
import { CircularProgress, Typography } from "@mui/material";
import { MembershipSelection } from "./membership-selection";
import { ReviewApplication } from "./review-application";
import { OverlayLoader } from "../../components/overlay-loader";
import { useDesignerApplicationContext } from "../../hooks/designer-application-context";
import { isWorkExperienceValid, validateProfessionalReferences, validateRevenue, validateApplication } from "./validations";

const titles = {
  1: "Work Experience",
  2: "Education",
  3: "Professional References",
  4: "Business Goals & Metrics",
  5: "Membership Tiers",
};

export const DesignerApplicationPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useUserContext();

  // Grab typed pieces from context
  const ctx = useDesignerApplicationContext();

  // Legacy fields (not in the narrowed type) via safe cast
  const {
    updateApplicationData,
    createApplication,
    saveDesignerApplication,
    refreshApplicationData: refreshApplicationDataV2,
    application: applicationFromContext,
    hasApplicationDataLoaded,
  } = ctx as any;

  const application = (applicationFromContext ?? {}) as Partial<DesignerApplication>;
  const applicationData = application;


  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [workExperienceValid, setWorkExperienceValid] = useState(false);
  const progressTitle = titles[step as 1 | 2 | 3 | 4 | 5];

  // Initialize work experience validation state
  useEffect(() => {
    setWorkExperienceValid(isWorkExperienceValid(application));
  }, [application]);

  // SSR-safe: compute small screen in effect rather than using window at init
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  useEffect(() => {
    const onResize = () => {
      if (typeof window !== "undefined") {
        setIsSmallScreen(window.innerWidth <= 580);
      }
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const shouldLeftCollapse = step >= 5 || isSmallScreen;

  const isApplicationEditable =
    application?.status === undefined ||
    application?.status === DesignerApplicationSteps.ToBeCompleted ||
    application?.status === DesignerApplicationSteps.CompleteApplication;

  const isEducationValid = () => true;

  const isBusinessGoalsValid = useCallback(
    () => {
      // Check if we have the latest application data from the context
      // The application object should be updated by the immediate updates from the form
      const validation = validateRevenue(application);
      const isValid = validation.isValid;
      return isValid;
    },
    [application]
  );

  const [contactReference, setContactReference] = useState(true);
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const hasMembershipSelection =
    !!application?.membershipTier || !!(application as any)?.membershipPlanId;

  const isReferencesValid = () => {
    const validation = validateProfessionalReferences(application?.references);
    const isValid = validation.isValid && contactReference;
    return isValid;
  };

  const stepSubmit = async (newStep: number) => {
    const workExperienceItems = application?.experience?.map((experience: any) => {
      const { __typename, ...rest } = experience || {};
      return rest;
    });

    // Block submission (step 7) if validation fails
    if (newStep === 7) {
      const validation = validateApplication(application);
      if (!validation.isValid) {
        alert(`Cannot submit application. Please fix the following:\n\n${validation.errors.join('\n')}`);
        return;
      }
      setIsSubmitting(true);
    } else {
      setStep(newStep);
    }

    if (
      newStep === 2 &&
      !hasApplicationDataLoaded &&
      !applicationId &&
      typeof createApplication === "function"
    ) {
      const appId = await createApplication({
        experience: workExperienceItems,
        designerId: (user as UserFromServer)?.id as string,
      });
      setApplicationId(appId);
    } else if (newStep > 2) {
      const status =
        newStep === 7 ? DesignerApplicationSteps.UnderReview : undefined;

      // ensure local state mirrors intended payload
      updateApplicationData({
        ...applicationData,
        _id: applicationId,
        status,
      });

      if (typeof saveDesignerApplication === "function") {
        await saveDesignerApplication({
          ...applicationData,
          _id: (applicationId ?? applicationData?._id) as string,
          status,
        });
      }

      if (newStep === 7) {
        // Refresh application data to ensure UI is updated with new status
        if (typeof refreshApplicationDataV2 === "function") {
          await refreshApplicationDataV2();
        }
        
        // Reset submitting state
        setIsSubmitting(false);
        
        // Show success message
        alert("Application submitted successfully! You can now schedule your vetting call.");
        
        // Navigate to dashboard
        navigate("/dashboard");
      }
    }
  };

  const saveAndExit = async () => {
    try {
      if (isApplicationEditable && typeof saveDesignerApplication === "function") {
        // Save without changing status - only save the current form data
        await saveDesignerApplication({
          ...applicationData,
          _id: applicationId,
          // Explicitly do NOT set status - let it remain as is
        });
      }
      navigate("/dashboard");
      if (isApplicationEditable && typeof refreshApplicationDataV2 === "function") {
        await refreshApplicationDataV2();
      }
    } catch (error) {
      // Still navigate to dashboard even if save fails
      navigate("/dashboard");
    }
  };

  // Redirect to login if not logged in
  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  }, [user, navigate]);

  // Auto-enable contactReference once prior steps are valid
  const hasSetContactReference = useRef(false);
  useEffect(() => {
    if (
      applicationData &&
      isBusinessGoalsValid() &&
      !contactReference &&
      !hasSetContactReference.current
    ) {
      hasSetContactReference.current = true;
      setContactReference(true);
    }
  }, [applicationData, contactReference, isBusinessGoalsValid]);

  // REMOVED: Auto-advancement to review step
  // This was causing the application to automatically advance to the review step
  // when the application status changed, which is not desired behavior.
  // Users should manually click Continue to advance steps.
  // 
  // If application is submitted / not editable, jump to review
  // useEffect(() => {
  //   if (application?.status && !isApplicationEditable) {
  //     setStep(6);
  //   }
  // }, [application?.status, isApplicationEditable]);

  return (
    <>
      <DualScreenLayout
        collapseLeft={shouldLeftCollapse}
        left={
          !shouldLeftCollapse && (
            <>
              <h4 className="font-body font-size-4 margin-0">DESIGNER</h4>
              <Typography
                variant={"display2" as any}
                component="h2"
                className="font-title font-size-6 margin-0"
              >
                MEMBERSHIP <br />
                APPLICATION
              </Typography>
              <Typography
                variant="body1"
                className="contain-text margin-0"
                sx={{ ml: "0 !important" }}
              >
                Tell us about your professional journey, current role, and
                provide necessary tax information for collaboration and
                potential partnerships.
              </Typography>
            </>
          )
        }
        right={
          <>
            {step <= 5 && (
              <div className="application-form-progress-bar site-wrapper">
                <StepBars
                  title={progressTitle}
                  description={
                    step === 5 &&
                    "Annual Home Design Guild Designer Membership Plan. By submitting this application, you understand this is your annual membership commitment. You agree that you are willing and able to make this Financial commitment should your application be approved. This is an annual (12-month) membership. you may cancel the membership at any time to cancel the $100 monthly platform fee. The $3,500 marketing fee is non-refundable. After cancellation, you will have access to the platform until the end of the current month."
                  }
                  stepCount={5}
                  currentStep={step}
                />
              </div>
            )}
            {loading && <CircularProgress sx={{ color: '#00A06D' }} />}
            {!loading && (
              <>
                {step === 1 && (
                  <StepFormContainer
                    step={1}
                    visible={step === 1}
                    isValid={workExperienceValid}
                    onSubmit={() => stepSubmit(2)}
                    onBack={() => navigate("/dashboard")}
                    saveAndExit={saveAndExit}
                  >
                    <WorkExperienceForm onValidationChange={setWorkExperienceValid} />
                  </StepFormContainer>
                )}

                {step === 2 && (
                  <StepFormContainer
                    step={2}
                    visible={step === 2}
                    isValid={isEducationValid()}
                    onSubmit={() => stepSubmit(3)}
                    onBack={() => setStep(1)}
                    saveAndExit={saveAndExit}
                  >
                    <EducationForm />
                  </StepFormContainer>
                )}

                {step === 3 && (
                  <StepFormContainer
                    step={3}
                    visible={step === 3}
                    isValid={isReferencesValid()}
                    onSubmit={() => stepSubmit(4)}
                    onBack={() => setStep(2)}
                    saveAndExit={saveAndExit}
                  >
                    <ReferenceForm
                      contactReference={contactReference}
                      setContactReference={setContactReference}
                    />
                  </StepFormContainer>
                )}

                {step === 4 && (
                  <StepFormContainer
                    step={4}
                    visible={step === 4}
                    isValid={isBusinessGoalsValid()}
                    onSubmit={() => stepSubmit(5)}
                    onBack={() => setStep(3)}
                    saveAndExit={saveAndExit}
                  >
                    <BusinessGoalsAndMetricsForm />
                  </StepFormContainer>
                )}

                {step === 5 && (
                  <StepFormContainer
                    step={5}
                    visible={step === 5}
                    isValid={hasMembershipSelection}
                    onSubmit={() => stepSubmit(6)}
                    onBack={() => setStep(4)}
                    saveAndExit={saveAndExit}
                    buttonText="I Agree"
                  >
                    <MembershipSelection />
                  </StepFormContainer>
                )}

                {step === 6 && (
                  <StepFormContainer
                    step={6}
                    visible={step === 6}
                    isValid={
                      isWorkExperienceValid(application) &&
                      isEducationValid() &&
                      isReferencesValid() &&
                      isBusinessGoalsValid() &&
                      hasMembershipSelection &&
                      isApplicationEditable
                    }
                    onSubmit={() => stepSubmit(7)}
                    onBack={() =>
                      isApplicationEditable ? setStep(5) : navigate("/dashboard")
                    }
                    saveAndExit={saveAndExit}
                    buttonText="Submit Application"
                  >
                    <ReviewApplication
                      isEditable={isApplicationEditable}
                      contactReference={contactReference}
                      setContactReference={setContactReference}
                    />
                  </StepFormContainer>
                )}
              </>
            )}
          </>
        }
      />
      {isSubmitting && <OverlayLoader loadingText="Submitting Application" />}
    </>
  );
};
